import React, { useState } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonCard,
  IonCardContent,
  IonList,
  IonIcon,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonToast,
  IonSpinner,
  IonText,
} from '@ionic/react';
import {
  logOutOutline,
  closeOutline,
  trashOutline,
  warningOutline,
} from 'ionicons/icons';
import { apiCall } from '@/util/api';
import { useUserContext } from '@/modules/user/useUserContext';
import { useSuppressibleNotices } from '@/contexts/SuppressibleNoticesContext';
import { useContactState } from '@/modules/contact/ContactStateContext';
import { StorageService } from '@/services/storage';
import { markAddressBookForRestore } from '@/modules/contact/addressBookBackup';
import { pxToRem } from '@/modules/config/constants';

interface ResetSnowballModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ResetSnowballModal: React.FC<ResetSnowballModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { clearAll: clearAllInterstitials } = useSuppressibleNotices();
  const {
    refreshUserContext,
    setFamiliarName,
    setAllowedName,
    setPhone,
    setEmail,
  } = useUserContext();
  const { dispatch: contactDispatch } = useContactState();
  const [clearIdentifyingInfo, setClearIdentifyingInfo] = useState(false);
  const [removeAllTraces, setRemoveAllTraces] = useState(false);
  const [clearAddressBook, setClearAddressBook] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastColor, setToastColor] = useState<'success' | 'danger'>('success');

  React.useEffect(() => {
    if (isOpen) {
      setClearIdentifyingInfo(false);
      setRemoveAllTraces(false);
      setClearAddressBook(false);
      setShowDeleteConfirmation(false);
      setShowToast(false);
    }
  }, [isOpen]);

  const handleResetSnowball = async () => {
    if (removeAllTraces) {
      setShowDeleteConfirmation(true);
      return;
    }

    await performResetSnowball();
  };

  const handleDeleteConfirmation = async () => {
    setShowDeleteConfirmation(false);
    await performResetSnowball();
  };

  const handleDeleteConfirmationCancel = () => {
    setShowDeleteConfirmation(false);
  };

  const performResetSnowball = async () => {
    setSubmitting(true);

    try {
      const response = await apiCall('/api/users/me/reset', {
        method: 'POST',
        body: JSON.stringify({ removeAllTraces }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to reset Snowball: ${errorText}`);
      }

      // Only mutate local state after the server-side reset has committed.
      // If the backend failed, leave name/phone/email and the address book
      // intact so the user can retry without losing their inputs.
      if (clearIdentifyingInfo) {
        await StorageService.clearNonAddressBookData();
        setFamiliarName(undefined);
        setAllowedName(undefined);
        setPhone(undefined);
        setEmail(undefined);
      }

      if (clearAddressBook) {
        contactDispatch({ type: 'SET_CONTACTS', payload: [] });
        // Unless the server backup is also being wiped, mark the device so the
        // next login restores the address book from the server copy rather than
        // pushing the now-empty local book up (which would destroy the backup).
        if (!removeAllTraces) {
          await markAddressBookForRestore();
        }
      }

      await refreshUserContext();

      await clearAllInterstitials();

      const message = clearIdentifyingInfo
        ? 'Snowball reset and identifying information cleared'
        : 'Snowball reset successfully. Your information is saved for next time.';
      setToastMessage(message);
      setToastColor('success');
      setShowToast(true);

      setTimeout(() => {
        onClose();
        if (onSuccess) {
          onSuccess();
        }
      }, 1000);
    } catch (error) {
      setToastMessage(
        error instanceof Error
          ? error.message
          : 'Failed to reset Snowball. Please try again.'
      );
      setToastColor('danger');
      setShowToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setClearIdentifyingInfo(false);
    setRemoveAllTraces(false);
    setClearAddressBook(false);
    setShowDeleteConfirmation(false);
    setShowToast(false);
    onClose();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Reset Snowball</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClose}>
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonList inset>
          <IonItem>
            <IonLabel>
              <h3>Clear your current session</h3>
              <p>Just re-verify the next time you use Snowball.</p>
            </IonLabel>
            <IonCheckbox
              slot="end"
              checked
              disabled
              aria-label="Clear your current session"
            />
          </IonItem>

          <IonItem>
            <IonLabel>
              <h3>Remove local data, except address book</h3>
              <p>You'll have to reenter your name, phone number and email.</p>
            </IonLabel>
            <IonCheckbox
              slot="end"
              checked={clearIdentifyingInfo}
              onIonChange={e => setClearIdentifyingInfo(e.detail.checked)}
              aria-label="Remove local device data"
            />
          </IonItem>

          <IonItem>
            <IonLabel>
              <h3>Wipe account data from our servers</h3>
              <p>
                Removes your email address, your address book backup, and logs
                of you sending and receiving messages.
              </p>
            </IonLabel>
            <IonCheckbox
              slot="end"
              checked={removeAllTraces}
              onIonChange={e => setRemoveAllTraces(e.detail.checked)}
              aria-label="Wipe your data from our servers"
            />
          </IonItem>

          <IonItem>
            <IonLabel>
              <h3>Clear local Snowball address book</h3>
              {!removeAllTraces && (
                <p>
                  Your address book will be automatically restored from its
                  server backup the next time you verify your phone.
                </p>
              )}
            </IonLabel>
            <IonCheckbox
              slot="end"
              checked={clearAddressBook}
              onIonChange={e => setClearAddressBook(e.detail.checked)}
              aria-label="Clear local Snowball address book"
            />
          </IonItem>
        </IonList>

        <div style={{ padding: '20px' }}>
          <IonButton
            expand="block"
            onClick={handleResetSnowball}
            disabled={submitting}
            color="danger"
          >
            {submitting ? (
              <IonSpinner name="crescent" />
            ) : (
              <IonIcon icon={logOutOutline} slot="start" />
            )}
            {submitting ? 'Resetting...' : 'Reset'}
          </IonButton>

          <IonButton
            expand="block"
            onClick={handleClose}
            disabled={submitting}
            fill="outline"
            style={{ marginTop: '10px' }}
          >
            Cancel
          </IonButton>
        </div>

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastColor}
          position="top"
          cssClass="app-toast"
        />
      </IonContent>

      {/* Delete Confirmation Submodal */}
      <IonModal isOpen={showDeleteConfirmation}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Confirm Deletion</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleDeleteConfirmationCancel}>
                <IonIcon slot="icon-only" icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: '20px' }}>
            <IonCard>
              <IonCardContent>
                <div style={{ marginBottom: '20px' }}>
                  <IonText color="danger">
                    <h2
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: pxToRem(18),
                      }}
                    >
                      <IonIcon icon={warningOutline} />
                      Warning
                    </h2>
                  </IonText>
                  <IonText color="medium">
                    <p style={{ marginTop: '10px', fontSize: pxToRem(14) }}>
                      This will delete any topic registrations and any history
                      of SMS messages that you've sent to your contacts. Are you
                      sure you want to do this?
                    </p>
                  </IonText>
                </div>

                <div style={{ padding: '20px 0' }}>
                  <IonButton
                    expand="block"
                    onClick={handleDeleteConfirmation}
                    disabled={submitting}
                    color="danger"
                  >
                    {submitting ? (
                      <IonSpinner name="crescent" />
                    ) : (
                      <IonIcon icon={trashOutline} />
                    )}
                    {submitting
                      ? 'Removing all traces of your profile...'
                      : 'OK'}
                  </IonButton>

                  <IonButton
                    expand="block"
                    onClick={handleDeleteConfirmationCancel}
                    disabled={submitting}
                    fill="outline"
                    style={{ marginTop: '10px' }}
                  >
                    Cancel
                  </IonButton>
                </div>
              </IonCardContent>
            </IonCard>
          </div>
        </IonContent>
      </IonModal>
    </IonModal>
  );
};

export default ResetSnowballModal;
